import { CircleAlert, CircleCheck, Info, LoaderCircle, TriangleAlert, X, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Toaster as Sonner } from 'sonner'
import { useTheme } from '@/components/layout/ThemeProvider'
import { cn } from '@/lib/utils'

function Chip({ icon: Icon, className, spin }: { icon: LucideIcon; className: string; spin?: boolean }) {
  return (
    <span className={cn('flex size-9 items-center justify-center rounded-lg border', className)}>
      <Icon className={cn('size-[18px]', spin && 'animate-spin')} />
    </span>
  )
}

export function Toaster() {
  const { t } = useTranslation()
  const { theme } = useTheme()

  return (
    <Sonner
      theme={theme}
      position="top-right"
      closeButton
      gap={10}
      offset={{ top: 16, right: 16 }}
      mobileOffset={{ top: 12, left: 12, right: 12 }}
      icons={{
        success: <Chip icon={CircleCheck} className="border-success/20 bg-success/12 text-success" />,
        error: <Chip icon={CircleAlert} className="border-destructive/20 bg-destructive/10 text-destructive" />,
        warning: <Chip icon={TriangleAlert} className="border-warning/25 bg-warning/12 text-warning" />,
        info: <Chip icon={Info} className="border-primary/20 bg-primary/10 text-primary" />,
        loading: <Chip icon={LoaderCircle} spin className="border-primary/20 bg-primary/10 text-primary" />,
        close: <X className="size-3.5" />,
      }}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: t('common.close'),
        classNames: {
          toast:
            'group relative flex min-h-16 w-full items-center gap-3 overflow-hidden rounded-xl border bg-popover p-3.5 pr-11 text-popover-foreground shadow-[0_18px_44px_-14px_color-mix(in_oklab,var(--shadow-color)_32%,transparent)] dark:shadow-[0_18px_44px_-14px_color-mix(in_oklab,var(--black)_70%,transparent)] [&>*]:transition-opacity [&[data-front=false][data-expanded=false]>*]:opacity-0',
          success: 'border-success/25 bg-gradient-to-r from-success/[0.07] to-transparent to-60%',
          error: 'border-destructive/25 bg-gradient-to-r from-destructive/[0.06] to-transparent to-60%',
          warning: 'border-warning/30 bg-gradient-to-r from-warning/[0.08] to-transparent to-60%',
          info: 'border-primary/20 bg-gradient-to-r from-primary/[0.06] to-transparent to-60%',
          loading: 'border-primary/20',
          icon: 'shrink-0',
          content: 'flex min-w-0 flex-1 flex-col justify-center gap-0.5',
          title: 'text-sm font-semibold leading-snug',
          description: 'text-[13px] leading-snug text-muted-foreground',
          closeButton:
            'absolute right-3 top-[18px] flex size-7 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-xs transition-colors hover:border-primary/30 hover:bg-primary/[0.06] hover:text-primary',
          actionButton:
            'inline-flex h-8 shrink-0 items-center rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-xs transition-colors hover:bg-primary/90',
          cancelButton:
            'inline-flex h-8 shrink-0 items-center rounded-md border border-destructive/25 bg-destructive/[0.06] px-3 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/12',
        },
      }}
    />
  )
}
